import React from "react";
import { useDispatch } from "react-redux";
import cx from "classnames";
import LinesEllipsis from "react-lines-ellipsis";
import { redeemGamificationReward } from "src/redux/actions/coaching/gamification";
import { GamificationReward } from "src/types/coaching/gamification";
import { BannerView } from "src/components/banner";
import Button from "src/components/button";
import Tooltip from "src/components/tooltip";

import styles from "./rewardsExplorer.module.scss";

import _ from "lodash";

const REWARD_REQUESTED_GIF = "/images/coaching/reward_requested.gif";
const defaultImage = "/images/collections/background.jpeg";

interface RewardCatalogCardProps {
  reward: GamificationReward;
  balance: number;
  redeeming: boolean;
  redeemSubmittingThis: boolean;
  hasPendingRequest: boolean;
  onRedeem?: (rewardId: string) => void;
}

export const PlaceholderRewardCatalogCard: React.FC = () => (
  <div className={cx(styles.catalogPlaceholderCard, styles.placeholder)}>
    <div className={styles.catalogPlaceholderImageWrap}>
      <div className={styles.block} />
    </div>
    <div className={styles.catalogPlaceholderBody}>
      <div className={cx(styles.catalogPlaceholderTitle, styles.block)} />
      <div className={cx(styles.catalogPlaceholderDesc, styles.block)} />
      <div className={styles.catalogPlaceholderFooter}>
        <div className={cx(styles.catalogPlaceholderCost, styles.block)} />
        <div className={cx(styles.catalogPlaceholderRedeem, styles.block)} />
      </div>
    </div>
  </div>
);

export const RewardCatalogCard: React.FC<RewardCatalogCardProps> = ({
  reward,
  balance,
  redeeming,
  redeemSubmittingThis,
  hasPendingRequest,
  onRedeem,
}) => {
  const dispatch = useDispatch();

  const [showNameTooltip, setShowNameTooltip] = React.useState(false);
  const [showDescTooltip, setShowDescTooltip] = React.useState(false);  

  const cost = reward.pointsCost ?? 0;
  const locked = cost > balance;
  const needExtra = locked ? Math.max(0, cost - balance) : 0;
  const canRedeem = !locked && !redeeming;
  const showRequested = redeemSubmittingThis;

  const banner = React.useMemo(() => _.get(reward, "banner"), [reward]);

  const handleRedeemClick = () => {
    if (!canRedeem) return;
    dispatch(redeemGamificationReward({ rewardId: reward.id }));
    onRedeem?.(reward.id);
  };

  return (
    <div
      className={cx(styles.catalogCard, {
        [styles.catalogCardLocked]: locked,
      })}
    >
      <div className={styles.catalogCardImageWrap}>
        <BannerView
          banner={banner as any}
          defaultBanner={defaultImage}
          overlay={false}
        />
        {locked && (
          <span className={styles.needBadge}>need {needExtra}</span>
        )}
      </div>
      <div className={styles.catalogCardBody}>
      <Tooltip
        content={reward.name}
        placement="top"
        disabled={!showNameTooltip}
      >
        <div className={styles.titleWrapper}>
          <h4 className={styles.catalogCardTitle}>
            <LinesEllipsis
              text={reward.name || ""}
              maxLine="1"
              basedOn="letters"
              ellipsis="..."
              onReflow={({ clamped }) => setShowNameTooltip(clamped)}
            />
          </h4>
        </div>
      </Tooltip>

   
      <Tooltip
        content={reward.description}
        placement="top"
        disabled={!showDescTooltip}
      >
        <div className={styles.descWrapper}>
          <p className={styles.catalogCardDescClamp}>
            <LinesEllipsis
              text={reward.description || ""}
              maxLine="2"
              basedOn="letters"
              ellipsis="..."
              onReflow={({ clamped }) => setShowDescTooltip(clamped)}
            />
          </p>
        </div>
      </Tooltip>

        <div className={styles.catalogCardFooter}>
          <span className={styles.catalogCost}>
            <i
              className={cx("paperflite-coins", styles.catalogCoin)}
              aria-hidden
            />
            {cost} points
          </span>
          {showRequested && (
            <span className={styles.catalogRequested}>
              <img
                src={REWARD_REQUESTED_GIF}
                alt=""
                className={styles.catalogRequestedGif}
              />
              requested reward
            </span>
          )}
          {!showRequested && (
            <Button
              variant="courses-primary"
              disabled={!canRedeem}
              onClick={handleRedeemClick}
              icon="paperflite-right-arrow"
              text="redeem"
            />
          )}
        </div>
      </div>
    </div>
  );
};